import { useEffect, useState, useRef } from "react";
import { motion } from "motion/react";
import { IconCheck, IconLoader2 } from "@tabler/icons-react";
import { Box, Flex, Group, Paper, Stack, Text, ThemeIcon } from "@mantine/core";
import { THINKING_STEPS } from "./utils";
import { generateAgentBrief } from "../../../../api/AgentsAPI";

export function ThinkingTimeline({ draftRef, onComplete, onError }) {
  const [active, setActive] = useState(0);
  const [isApiDone, setIsApiDone] = useState(false);
  const apiResultRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchBrief = async () => {
      let input = draftRef.current.companyInput;
      const urlRegex =
        /(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-z]{2,10}\b(?:[-a-zA-Z0-9@:%_+.~#?&//=]*)/i;
      const match = input.match(urlRegex);

      let extractedLink = "";
      if (match && match[0]) {
        extractedLink = /^https?:\/\//i.test(match[0])
          ? match[0]
          : `https://${match[0]}`;
      }

      try {
        // Remplacer par votre fonction d'appel API réelle
        const response = await generateAgentBrief({
          website_url: extractedLink || null,
          activity_description: input,
        });

        if (isMounted) {
          apiResultRef.current = response.data.brief;
          setIsApiDone(true);
        }
      } catch (error) {
        console.error("Erreur lors de la génération :", error);
        onError?.(error);
        if (isMounted) setIsApiDone(true);
      }
    };

    fetchBrief();

    return () => {
      isMounted = false;
    };
  }, [draftRef.current.companyInput, draftRef, onError]);

  useEffect(() => {
    // Si on a validé toutes les étapes
    if (active >= THINKING_STEPS.length) {
      // On s'assure que l'API a répondu avant de conclure
      if (isApiDone) {
        // On passe le résultat de l'API au composant parent
        const t = setTimeout(() => onComplete(apiResultRef.current), 700);
        return () => clearTimeout(t);
      }
      return;
    }

    // Si on est à la toute dernière étape de réflexion, on bloque
    // l'animation tant que l'API n'a pas renvoyé sa réponse.
    if (active === THINKING_STEPS.length - 1 && !isApiDone) {
      return;
    }

    // Sinon, on avance normalement
    const delay = 620 + Math.random() * 420 + 1500;
    const t = setTimeout(() => setActive((a) => a + 1), delay);
    return () => clearTimeout(t);
  }, [active, isApiDone, onComplete]);

  const progress = Math.min(
    100,
    Math.round((active / THINKING_STEPS.length) * 100),
  );

  return (
    <Paper
      radius={32}
      p="xl"
      withBorder
      style={{
        maxWidth: 800,
        width: "100%",
        margin: "0 auto",
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        backdropFilter: "blur(12px)",
        boxShadow: "0 24px 60px -30px rgba(30,41,120,0.35)",
      }}
    >
      {/* HEADER : Titre et Icône animée */}
      <Group gap="md" wrap="nowrap" align="center">
        <Box
          pos="relative"
          w={44}
          h={44}
          style={{ display: "grid", placeItems: "center" }}
        >
          <motion.div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "16px",
              border: "1px solid var(--mantine-color-primary-3)",
              backgroundColor: "var(--mantine-color-primary-0)",
            }}
            animate={{ scale: [1, 1.25, 1], opacity: [0.6, 0, 0.6] }}
            transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
          />
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 2,
              repeat: Number.POSITIVE_INFINITY,
              ease: "linear",
            }}
            style={{
              display: "flex",
              zIndex: 1,
              color: "var(--mantine-color-primary-6)",
            }}
          >
            <IconLoader2 size={20} />
          </motion.div>
        </Box>

        <Box>
          <Text size="xl" fw={600}>
            Votre agent prend vie
          </Text>
          <Text size="lg" c="dimmed">
            L'IA analyse votre entreprise en profondeur…
          </Text>
        </Box>
      </Group>

      {/* BARRE DE PROGRESSION */}
      <Box
        mt={20}
        h={8}
        w="100%"
        style={{
          borderRadius: 9999,
          backgroundColor: "var(--mantine-color-gray-2)",
          overflow: "hidden",
        }}
      >
        <motion.div
          style={{
            height: "100%",
            borderRadius: 9999,
            backgroundColor: "var(--mantine-color-primary-6)",
          }}
          animate={{ width: `${progress}%` }}
          transition={{ type: "spring", stiffness: 80, damping: 20 }}
        />
      </Box>

      {/* LISTE DES ÉTAPES */}
      <Stack mt="sm" gap={6}>
        {THINKING_STEPS.map((step, i) => {
          const done = i < active;
          const current = i === active;
          const pending = i > active;

          return (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: pending ? 0.45 : 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Flex
                align="center"
                gap="md"
                style={{
                  padding: "8px 10px",
                  borderRadius: "16px",
                  backgroundColor: current
                    ? "var(--mantine-color-gray-1)"
                    : "transparent",
                  transition: "background-color 0.2s ease",
                }}
              >
                <Box mt={2}>
                  {done ? (
                    <ThemeIcon
                      size={32}
                      radius="xl"
                      color="primary"
                      variant="light"
                    >
                      <IconCheck size={14} />
                    </ThemeIcon>
                  ) : current ? (
                    <ThemeIcon
                      size={32}
                      radius="xl"
                      variant="outline"
                      color="primary"
                    >
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 2,
                          repeat: Number.POSITIVE_INFINITY,
                          ease: "linear",
                        }}
                        style={{ display: "flex" }}
                      >
                        <IconLoader2 size={14} />
                      </motion.div>
                    </ThemeIcon>
                  ) : (
                    <ThemeIcon
                      size={32}
                      radius="xl"
                      variant="outline"
                      color="gray.4"
                    >
                      <Box
                        w={6}
                        h={6}
                        style={{
                          borderRadius: "50%",
                          backgroundColor: "currentColor",
                        }}
                      />
                    </ThemeIcon>
                  )}
                </Box>

                <Box style={{ minWidth: 0, flex: 1 }}>
                  <Text
                    size="lg"
                    fw={500}
                    c={done || current ? "dark" : "dimmed"}
                  >
                    {step.label}
                  </Text>

                  {current && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      style={{ overflow: "hidden" }}
                    >
                      <Text size="xs" c="dimmed">
                        {step.detail}
                      </Text>
                    </motion.div>
                  )}
                </Box>
              </Flex>
            </motion.div>
          );
        })}
      </Stack>
    </Paper>
  );
}
